import { FormInput } from 'models/form';
import { CharacteristicsRk } from 'utils/characteristic';
import { ValidationPatterns, YupType } from 'utils/constants';
import { CharacteristicSpecificationInterface } from 'models/characteristics';
import { createValidationSchema } from 'utils';

export enum DeliveryPointValidationArguments {
  RESERVED_CAPACITY_GUARD_PERIOD_START_RESTRICTION_OFFSET_REQUIRED = 'reserved_capacity_guard_period_start_restriction_offset-required',
  RESERVED_CAPACITY_GUARD_PERIOD_START_RESTRICTION_OFFSET_NUMBER = 'reserved_capacity_guard_period_start_restriction_offset-number',
  RESERVED_CAPACITY_GUARD_PHASE_ONE_RESERVE_REQUIRED = 'reserved_capacity_guard_phase_one_reserve-required',
  RESERVED_CAPACITY_GUARD_PHASE_ONE_RESERVE_NUMBER = 'reserved_capacity_guard_phase_one_reserve-number',
  RESERVED_CAPACITY_GUARD_PHASE_ONE_RESERVE_MAX = 'reserved_capacity_guard_phase_one_reserve-max',
  RESERVED_CAPACITY_GUARD_PHASE_TWO_RESERVE_REQUIRED = 'reserved_capacity_guard_phase_two_reserve-required',
  RESERVED_CAPACITY_GUARD_PHASE_TWO_RESERVE_NUMBER = 'reserved_capacity_guard_phase_two_reserve-number',
  RESERVED_CAPACITY_GUARD_PHASE_TWO_RESERVE_MAX = 'reserved_capacity_guard_phase_two_reserve-max',
  RESERVED_CAPACITY_GUARD_EARLIEST_RESTRICTION_START_OFFSET_REQUIRED = 'reserved_capacity_guard_earliest_restriction_start_offset-required',
  RESERVED_CAPACITY_GUARD_EARLIEST_RESTRICTION_START_OFFSET_NUMBER = 'reserved_capacity_guard_earliest_restriction_start_offset-number',
  RESERVED_CAPACITY_GUARD_CHECK_PERIODICITY_REQUIRED = 'reserved_capacity_guard_check_periodicity-required',
  RESERVED_CAPACITY_GUARD_CHECK_ALGORITHM_REQUIRED = 'reserved_capacity_guard_check_algorithm-required',
  RESERVED_CAPACITY_UTILIZATION_ALARM_UTILIZATION_THRESHOLD_REQUIRED = 'reserved_capacity_utilization_alarm_utilization_threshold-required',
  RESERVED_CAPACITY_UTILIZATION_ALARM_UTILIZATION_THRESHOLD_NUMBER = 'reserved_capacity_utilization_alarm_utilization_threshold-number',
  RESERVED_CAPACITY_UTILIZATION_ALARM_TIME_THRESHOLD_REQUIRED = 'reserved_capacity_utilization_alarm_time_threshold-required',
  RESERVED_CAPACITY_UTILIZATION_ALARM_TIME_THRESHOLD_NUMBER = 'reserved_capacity_utilization_alarm_time_threshold-number'
}

// ==============================|| SCHEMA ||============================== //

const getDeliveryPointReservedCapacityGuardingCharacteristicsInputs = (
  maxReservedCapacityReserve: string
): FormInput[] => [
  {
    id: CharacteristicsRk.reserved_capacity_guard_period_start_restriction_offset,
    validation: {
      type: YupType.STRING,
      methods: [
        {
          methodType: 'trim'
        },
        {
          methodType: 'required',
          args: [
            DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_PERIOD_START_RESTRICTION_OFFSET_REQUIRED
          ]
        },
        {
          methodType: 'matches',
          args: [
            ValidationPatterns.POSITIVE_INTEGER,
            DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_PERIOD_START_RESTRICTION_OFFSET_NUMBER
          ]
        }
      ]
    }
  },
  {
    id: CharacteristicsRk.reserved_capacity_guard_phase_one_reserve,
    validation: {
      type: YupType.STRING,
      methods: [
        {
          methodType: 'trim'
        },
        {
          methodType: 'required',
          args: [
            DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_PHASE_ONE_RESERVE_REQUIRED
          ]
        },
        {
          methodType: 'matches',
          args: [
            ValidationPatterns.NUMBER_WITH_MAX_TWO_DECIMAL,
            DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_PHASE_ONE_RESERVE_NUMBER
          ]
        },
        {
          methodType: 'test',
          args: [
            'maxReservedCapacityReserve',
            DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_PHASE_ONE_RESERVE_MAX,
            (value: string) => {
              return Number(value) <= Number(maxReservedCapacityReserve);
            }
          ]
        }
      ]
    }
  },
  {
    id: CharacteristicsRk.reserved_capacity_guard_phase_two_reserve,
    validation: {
      type: YupType.STRING,
      methods: [
        {
          methodType: 'trim'
        },
        {
          methodType: 'required',
          args: [
            DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_PHASE_TWO_RESERVE_REQUIRED
          ]
        },
        {
          methodType: 'matches',
          args: [
            ValidationPatterns.NUMBER_WITH_MAX_TWO_DECIMAL,
            DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_PHASE_TWO_RESERVE_NUMBER
          ]
        },
        {
          methodType: 'test',
          args: [
            'maxReservedCapacityReserve',
            DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_PHASE_TWO_RESERVE_MAX,
            (value: string) => {
              return Number(value) <= Number(maxReservedCapacityReserve);
            }
          ]
        }
      ]
    }
  },
  {
    id: CharacteristicsRk.reserved_capacity_guard_earliest_restriction_start_offset,
    validation: {
      type: YupType.STRING,
      methods: [
        {
          methodType: 'trim'
        },
        {
          methodType: 'required',
          args: [
            DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_EARLIEST_RESTRICTION_START_OFFSET_REQUIRED
          ]
        },
        {
          methodType: 'matches',
          args: [
            ValidationPatterns.POSITIVE_INTEGER,
            DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_EARLIEST_RESTRICTION_START_OFFSET_NUMBER
          ]
        }
      ]
    }
  },
  {
    id: CharacteristicsRk.reserved_capacity_guard_check_periodicity,
    validation: {
      type: YupType.STRING,
      methods: [
        {
          methodType: 'trim'
        },
        {
          methodType: 'required',
          args: [
            DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_CHECK_PERIODICITY_REQUIRED
          ]
        }
      ]
    }
  },
  {
    id: CharacteristicsRk.reserved_capacity_guard_check_algorithm,
    validation: {
      type: YupType.STRING,
      methods: [
        {
          methodType: 'trim'
        },
        {
          methodType: 'required',
          args: [
            DeliveryPointValidationArguments.RESERVED_CAPACITY_GUARD_CHECK_ALGORITHM_REQUIRED
          ]
        }
      ]
    }
  },
  {
    id: CharacteristicsRk.reserved_capacity_utilization_alarm_utilization_threshold,
    validation: {
      type: YupType.STRING,
      methods: [
        {
          methodType: 'trim'
        },
        {
          methodType: 'required',
          args: [
            DeliveryPointValidationArguments.RESERVED_CAPACITY_UTILIZATION_ALARM_UTILIZATION_THRESHOLD_REQUIRED
          ]
        },
        {
          methodType: 'matches',
          args: [
            ValidationPatterns.POSITIVE_INTEGER,
            DeliveryPointValidationArguments.RESERVED_CAPACITY_UTILIZATION_ALARM_UTILIZATION_THRESHOLD_NUMBER
          ]
        }
      ]
    }
  },
  {
    id: CharacteristicsRk.reserved_capacity_utilization_alarm_time_threshold,
    validation: {
      type: YupType.STRING,
      methods: [
        {
          methodType: 'trim'
        },
        {
          methodType: 'required',
          args: [
            DeliveryPointValidationArguments.RESERVED_CAPACITY_UTILIZATION_ALARM_TIME_THRESHOLD_REQUIRED
          ]
        },
        {
          methodType: 'matches',
          args: [
            ValidationPatterns.POSITIVE_INTEGER,
            DeliveryPointValidationArguments.RESERVED_CAPACITY_UTILIZATION_ALARM_TIME_THRESHOLD_NUMBER
          ]
        }
      ]
    }
  }
];

export const createDeliveryPointReservedCapacityGuardingCharacteristicsValidationSchema =
  (
    characteristicSpecifications:
      | CharacteristicSpecificationInterface[]
      | undefined,
    maxReservedCapacityReserve: string
  ) => {
    if (!characteristicSpecifications) return;

    return createValidationSchema(
      getDeliveryPointReservedCapacityGuardingCharacteristicsInputs(
        maxReservedCapacityReserve
      ),
      characteristicSpecifications
    );
  };
